import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChat, conversationDisplayTime, type ChatScope } from "./context";
import { useChatListModelContext } from "./listModelContext";
import { VirtualizedList } from "./VirtualizedList";
import { ConversationAvatar } from "./ConversationAvatar";
import type { ChatListRow, ChatType } from "../core/useChatListModel";
import { conversationPreviewText } from "../core/chatThreadModel";
import {
  CARD_GAP,
  CONVERSATION_ROW_HEIGHT,
  EMPTY_HINT_ROW_HEIGHT,
  PERSON_ROW_HEIGHT,
  SEARCH_MAX_LENGTH,
  SUB_HEADER_ROW_HEIGHT,
  TYPE_HEADER_ROW_HEIGHT,
} from "../portalChat.constants";
import { deleteSectionRequest, type ChatConversation, type ChatSection } from "../api/portalChat.api";
import {
  NewChatDialog,
  SectionsDialog,
  ProjectOrderDialog,
  HideConfirmDialog,
  SectionDialog,
  type SectionDialogTarget,
} from "./ChatListDialogs";

const SCOPES: readonly ChatScope[] = ["all", "direct", "group", "project"];

/** Section scope for a type: direct/group have sections, spaces are ordered by project. */
const sectionScopeOf = (type: ChatType): "direct" | "group" | null =>
  type === "direct" ? "direct" : type === "group" ? "group" : null;

/**
 * Card boundaries over the flat row list. Rows are absolutely positioned by react-virtualized, so
 * a card is emulated per row: a type header (or the very first row) opens a card, the row before
 * the next type header closes it. Single-type scopes therefore render as one headerless card.
 */
function cardBounds(rows: readonly ChatListRow[]): { first: boolean; last: boolean }[] {
  return rows.map((row, index) => {
    const next = rows[index + 1];
    return {
      first: index === 0 || row.kind === "typeHeader",
      last: next === undefined || next.kind === "typeHeader",
    };
  });
}

/** Dashed empty block (design "empty view"): icon disc, title, body, optional CTA. */
function EmptyBlock({
  title,
  body,
  cta,
  onCta,
}: {
  readonly title: string;
  readonly body: string;
  readonly cta?: string | undefined;
  readonly onCta?: (() => void) | undefined;
}) {
  return (
    <div className="mx-0.5 my-3 rounded-[0.625rem] border border-dashed border-[#C3CCD3] bg-[#F7F9FB] px-5 py-8 text-center">
      <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#E4F1F9]">
        <IconComponent name="Chat" width="1.25rem" height="1.25rem" />
      </span>
      <div className="text-[0.9375rem] font-bold text-[#1C2228]">{title}</div>
      <div className="mx-auto mt-1.5 max-w-[15.625rem] text-xs leading-relaxed text-[#6B757D]">{body}</div>
      {cta && onCta && (
        <div className="mt-4 flex justify-center">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={onCta}>
            {cta}
          </ButtonComponent>
        </div>
      )}
    </div>
  );
}

/**
 * Left pane: view picker + maximise/close, Create / search / Unread toggle, and the card list
 * (type cards in All Chats, light-blue section / project sub-headers, bounded rows). Every list
 * rule lives in the shared model (../core/useChatListModel) — this pane only renders rows.
 */
function ChatListPane({
  connected,
  maximized,
  onToggleMaximize,
  onClose,
}: {
  readonly connected: boolean;
  readonly maximized: boolean;
  readonly onToggleMaximize: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const { scope, unreadOnly } = chat;
  const {
    search,
    setSearch,
    searchLower,
    scopeLabel,
    typeLabel,
    listRows,
    toggleSectionCollapsed,
    toggleProjectCollapsed,
    toggleTypeCollapsed,
    toggleUnreadOnly,
    openConversationRow,
    startDirectWith,
  } = useChatListModelContext();

  const [searchOpen, setSearchOpen] = useState(false);
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState<HTMLElement | null>(null);
  const [barMenuAnchor, setBarMenuAnchor] = useState<HTMLElement | null>(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [sectionsScope, setSectionsScope] = useState<"direct" | "group" | null>(null);
  const [showProjectOrder, setShowProjectOrder] = useState(false);
  const [sectionDialog, setSectionDialog] = useState<SectionDialogTarget | null>(null);
  const [hideCandidate, setHideCandidate] = useState<ChatConversation | null>(null);
  const [menuFor, setMenuFor] = useState<{ conversation: ChatConversation; anchor: HTMLElement } | null>(null);
  const [sectionHeaderMenu, setSectionHeaderMenu] = useState<{ section: ChatSection; anchor: HTMLElement } | null>(
    null,
  );
  const [projectHeaderMenuAnchor, setProjectHeaderMenuAnchor] = useState<HTMLElement | null>(null);
  const [typeHeaderMenu, setTypeHeaderMenu] = useState<{ type: ChatType; anchor: HTMLElement } | null>(null);

  // PRD §4.7: switching scope clears the search (model-owned) — collapse the field with it.
  useEffect(() => {
    setSearchOpen(false);
  }, [scope]);

  // PRD §4.6: open menus close when the panel is maximised or restored.
  useEffect(() => {
    setMenuFor(null);
    setScopeMenuAnchor(null);
    setBarMenuAnchor(null);
    setSectionHeaderMenu(null);
    setProjectHeaderMenuAnchor(null);
    setTypeHeaderMenu(null);
  }, [chat.menuEpoch]);

  const bounds = useMemo(() => cardBounds(listRows), [listRows]);

  const scopeOptionLabel = (option: ChatScope) =>
    option === "all" ? t("All Chats") : typeLabel(option === "project" ? "space" : option);

  const closeSearch = () => {
    setSearch("");
    setSearchOpen(false);
  };

  const onCreate = () => {
    if (scope === "project") {
      // Project chats are created with their Space (PRD §4.10) — a toast, never an inline banner.
      chat.showToast(
        t(
          "Project chats cannot be created here. A chat is created with each project, and you are added to it automatically when you join.",
        ),
        false,
      );
      return;
    }
    setShowNewChat(true);
  };

  /** Type-card / bar ••• menu: Create a section + Manage sections (projects: order only). */
  const openTypeMenu = (type: ChatType, anchor: HTMLElement) => setTypeHeaderMenu({ type, anchor });

  const renderTypeHeader = (row: Extract<ChatListRow, { kind: "typeHeader" }>) => {
    const type = row.typeHeader;
    const collapsible = type !== undefined;
    return (
      <div
        role={collapsible ? "button" : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onClick={type === undefined ? undefined : () => toggleTypeCollapsed(type)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && type !== undefined) toggleTypeCollapsed(type);
        }}
        className={`flex h-full items-center gap-2 px-3.5 ${collapsible ? "cursor-pointer hover:bg-[#F7F9FB]" : ""}`}
      >
        <span className="min-w-0 flex-1 truncate text-[0.9rem] font-bold text-[#1C2228]">{row.label}</span>
        {type !== undefined && (
          <span
            role="button"
            tabIndex={-1}
            title={t("Section options")}
            className="rounded-md p-1 hover:bg-[#EEF2F5]"
            onClick={(e) => {
              e.stopPropagation();
              openTypeMenu(type, e.currentTarget);
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <IconComponent name="threedots" width="0.9rem" height="0.9rem" />
          </span>
        )}
        {collapsible && (
          <IconComponent
            name="BottomArrow"
            width="0.7rem"
            height="0.7rem"
            className={row.collapsed ? "-rotate-90 transition-transform" : "transition-transform"}
          />
        )}
      </div>
    );
  };

  /** Light-blue uppercase sub-header shared by user sections and project blocks. */
  const renderSubHeader = ({
    label,
    count,
    collapsed,
    highlighted,
    onToggle,
    onMenu,
  }: {
    label: string;
    count: number;
    collapsed: boolean;
    highlighted: boolean;
    onToggle: () => void;
    onMenu: (anchor: HTMLElement) => void;
  }) => (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter") onToggle();
      }}
      className={`flex h-full cursor-pointer items-center gap-2 border-t border-[#EEF2F5] px-3.5 ${
        highlighted ? "bg-[#E4F1F9] text-[#1F5F80]" : "bg-[#F4FAFD] text-[#4A545C]"
      } hover:bg-[#EEF2F5]`}
    >
      <IconComponent
        name="BottomArrow"
        width="0.6rem"
        height="0.6rem"
        className={collapsed ? "-rotate-90 transition-transform" : "transition-transform"}
      />
      <span className="min-w-0 flex-1 truncate text-[0.6875rem] font-bold uppercase tracking-[0.1em]">{label}</span>
      <span className="text-[0.6875rem] text-[#9AA5AD]">{count}</span>
      <span
        role="button"
        tabIndex={-1}
        title={t("Section options")}
        className="rounded-md p-0.5 hover:bg-white"
        onClick={(e) => {
          e.stopPropagation();
          onMenu(e.currentTarget);
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <IconComponent name="threedots" width="0.8rem" height="0.8rem" />
      </span>
    </div>
  );

  const renderConversation = (conversation: ChatConversation, hiddenBadge: boolean, first: boolean) => {
    const isSelected = chat.selectedId === conversation._id;
    const open = () => {
      openConversationRow(conversation);
    };
    const preview = conversationPreviewText(conversation, t("This message was deleted"), t("Attachment"));
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter") open();
        }}
        className={`group flex h-full cursor-pointer items-start gap-2.5 px-3.5 py-2.5 ${
          first ? "" : "border-t border-[#EEF2F5]"
        } ${isSelected ? "bg-[#E4F1F9]" : "hover:bg-[#F7F9FB]"}`}
      >
        <ConversationAvatar conversation={conversation} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="min-w-0 flex-1 truncate text-sm font-bold text-[#1C2228]">{conversation.name}</span>
            {conversation.muted && (
              <IconComponent name="SpeakerOff" width="0.8rem" height="0.8rem" className="opacity-60" />
            )}
            {hiddenBadge && (
              <span className="rounded-xs bg-[#EEF2F5] px-1 text-[0.6rem] text-[#6B757D]">{t("Hidden")}</span>
            )}
            <span className="whitespace-nowrap text-[0.7rem] text-[#6B757D]">
              {conversation.lastMessageDate ? conversationDisplayTime(conversation.lastMessageDate) : ""}
            </span>
            <span
              role="button"
              tabIndex={-1}
              title={t("Chat options")}
              className="rounded-md p-0.5 opacity-0 hover:bg-[#EEF2F5] group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                setMenuFor({ conversation, anchor: e.currentTarget });
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <IconComponent name="threedots" width="0.85rem" height="0.85rem" />
            </span>
          </div>
          <div className="mt-0.5 flex items-end gap-2">
            <span className="line-clamp-2 min-w-0 flex-1 text-xs leading-snug text-[#6B757D]">{preview}</span>
            {/* PRD §4.5: a muted conversation still shows its in-row unread count. */}
            {conversation.unreadCount > 0 && (
              <span
                className={`flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[0.6875rem] font-bold text-white ${
                  conversation.muted ? "bg-[#9AA5AD]" : "bg-[#1F9D55]"
                }`}
              >
                {conversation.unreadCount > 99 ? "99+" : conversation.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRowContent = (row: ChatListRow, index: number) => {
    const first = bounds[index]?.first ?? false;
    switch (row.kind) {
      case "typeHeader":
        return renderTypeHeader(row);
      case "sectionHeader": {
        const section = chat.sections.find((s) => String(s._id) === row.sectionId);
        return renderSubHeader({
          label: row.label,
          count: row.count,
          collapsed: row.collapsed,
          highlighted: false,
          onToggle: () => toggleSectionCollapsed(row.sectionId),
          onMenu: (anchor) => {
            if (section) setSectionHeaderMenu({ section, anchor });
          },
        });
      }
      case "projectHeader":
        return renderSubHeader({
          label: row.label,
          count: row.count,
          collapsed: row.collapsed,
          highlighted: row.isCurrentProject,
          onToggle: () => toggleProjectCollapsed(row.projectId),
          onMenu: (anchor) => setProjectHeaderMenuAnchor(anchor),
        });
      case "emptyHint":
        return (
          <div className="flex h-full items-center justify-center border-t border-[#EEF2F5] px-3 text-xs text-[#9AA5AD]">
            {t("No chats here yet — move one in from a chat's ••• menu.")}
          </div>
        );
      case "person":
        return (
          <button
            type="button"
            onClick={() => void startDirectWith(row.user)}
            className={`flex h-full w-full items-center gap-2.5 px-3.5 text-start hover:bg-[#F7F9FB] ${
              first ? "" : "border-t border-[#EEF2F5]"
            }`}
          >
            <UserNameTagComponent showAvatarOnly id={String(row.user._id)} size="XS" isCircle />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-[#1C2228]">{row.user.fullName}</span>
              <span className="block truncate text-xs text-[#6B757D]">{row.user.designation ?? row.user.email}</span>
            </span>
          </button>
        );
      case "conversation":
        return renderConversation(row.conversation, row.hiddenBadge, first);
    }
  };

  const renderListRow = (row: ChatListRow, index: number) => {
    const { first, last } = bounds[index] ?? { first: false, last: false };
    const gapAbove = row.kind === "typeHeader" && index > 0;
    return (
      <div className="h-full" style={gapAbove ? { paddingTop: CARD_GAP } : undefined}>
        <div
          className={`h-full overflow-hidden border-x border-[#EEF2F5] bg-white ${
            first ? "rounded-t-[0.625rem] border-t" : ""
          } ${last ? "rounded-b-[0.625rem] border-b" : ""}`}
        >
          {renderRowContent(row, index)}
        </div>
      </div>
    );
  };

  const rowHeight = (row: ChatListRow, index: number) => {
    switch (row.kind) {
      case "typeHeader":
        return TYPE_HEADER_ROW_HEIGHT + (index > 0 ? CARD_GAP : 0);
      case "sectionHeader":
      case "projectHeader":
        return SUB_HEADER_ROW_HEIGHT;
      case "emptyHint":
        return EMPTY_HINT_ROW_HEIGHT;
      case "person":
        return PERSON_ROW_HEIGHT;
      case "conversation":
        return CONVERSATION_ROW_HEIGHT;
    }
  };

  const renderEmptyState = () => {
    if (searchLower) {
      return (
        <EmptyBlock
          title={t("No conversations match {{query}}", { query: search.trim() })}
          body={t("Try a person's name, a chat name or a word from a recent message.")}
          cta={t("Clear search")}
          onCta={closeSearch}
        />
      );
    }
    if (unreadOnly) {
      return (
        <EmptyBlock
          title={t("No unread messages")}
          body={t("You are up to date on every conversation here. Switch off Unread to see them all again.")}
        />
      );
    }
    if (scope === "project") {
      return (
        <EmptyBlock
          title={t("No project chats yet")}
          body={t("Project chats come with each project space. You are added automatically when you join one.")}
        />
      );
    }
    if (scope === "direct") {
      return (
        <EmptyBlock
          title={t("No direct chats yet")}
          body={t("Start a one-to-one conversation with anyone on your projects.")}
          cta={t("New direct chat")}
          onCta={() => setShowNewChat(true)}
        />
      );
    }
    if (scope === "group") {
      return (
        <EmptyBlock
          title={t("No group chats yet")}
          body={t("Bring a few people together around an RFI, a package or a handover.")}
          cta={t("New group chat")}
          onCta={() => setShowNewChat(true)}
        />
      );
    }
    // PRD §4.16 distinguishes "none in view" (chats exist but are all hidden) from a brand-new user.
    if (chat.conversations.length) {
      return (
        <EmptyBlock
          title={t("No chats yet.")}
          body={t("Start a direct or group chat with anyone on your projects.")}
          cta={t("New chat")}
          onCta={() => setShowNewChat(true)}
        />
      );
    }
    // Brand-new user start layout (PRD §4.16): title, two option cards, project-chat note.
    return (
      <div className="px-0.5 pb-2 pt-4">
        <div className="text-base font-bold text-[#1C2228]">{t("Start your first conversation")}</div>
        <div className="mt-1 text-xs leading-relaxed text-[#6B757D]">
          {t("Chats sit alongside your projects, so what you discuss stays next to the work.")}
        </div>
        <div className="mt-4 flex flex-col gap-2">
          {(["direct", "group"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setShowNewChat(true)}
              className="flex w-full items-center gap-3 rounded-[0.625rem] border border-[#DDE3E8] bg-white p-3 text-start hover:border-[#6BBBE3] hover:bg-[#F4FAFD]"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E4F1F9]">
                <IconComponent name={option === "direct" ? "Chat" : "usersIcon"} width="1rem" height="1rem" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-[#1C2228]">
                  {option === "direct" ? t("Direct chat") : t("Group chat")}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-[#6B757D]">
                  {option === "direct"
                    ? t("One-to-one with anyone on your projects")
                    : t("A few people around an RFI, package or handover")}
                </span>
              </span>
            </button>
          ))}
        </div>
        <div className="mt-4 border-t border-[#EEF2F5] pt-3 text-xs leading-relaxed text-[#9AA5AD]">
          {t("Project chats appear here automatically when you are added to a project space.")}
        </div>
      </div>
    );
  };

  const menuConversation = menuFor?.conversation;
  const menuSectionScope = menuConversation ? sectionScopeOf(menuConversation.type) : null;
  const menuSections = menuSectionScope
    ? chat.sections.filter((s) => s.scope === menuSectionScope).sort((a, b) => a.order - b.order)
    : [];

  /** MUI Menu children must not be Fragments — the "Move to section" block is built as a flat array. */
  const moveToSectionItems =
    menuConversation && menuSectionScope
      ? [
          <MenuItemComponent key="move-header" disabled height="2rem" sx={{ opacity: "1 !important" }}>
            <span className="text-[0.65rem] font-bold uppercase tracking-[0.1em] text-[#9AA5AD]">
              {t("Move to section")}
            </span>
          </MenuItemComponent>,
          <MenuItemComponent
            key="move-none"
            onClick={() => {
              void chat.updateConversationState(menuConversation._id, { sectionId: null });
              setMenuFor(null);
            }}
          >
            <span className={menuConversation.sectionId ? "" : "font-bold"}>{t("No section")}</span>
          </MenuItemComponent>,
          ...menuSections.map((section) => (
            <MenuItemComponent
              key={String(section._id)}
              onClick={() => {
                void chat.updateConversationState(menuConversation._id, { sectionId: String(section._id) });
                setMenuFor(null);
              }}
            >
              <span className={String(menuConversation.sectionId ?? "") === String(section._id) ? "font-bold" : ""}>
                {section.name}
              </span>
            </MenuItemComponent>
          )),
          <MenuItemComponent
            key="move-create"
            onClick={() => {
              setMenuFor(null);
              setSectionDialog({ scope: menuSectionScope, assignConversationId: menuConversation._id });
            }}
          >
            {t("Create new section")}
          </MenuItemComponent>,
        ]
      : [];

  return (
    <div className="flex w-94 min-w-[18.75rem] shrink-0 flex-col border-e border-[#EEF2F5] bg-white">
      {/* Row 1: view picker + maximise / close */}
      <div className="flex items-center justify-between gap-2 px-4 pb-3 pt-4">
        <button
          type="button"
          data-testid="portalChatScope"
          className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-[#EEF2F5]"
          onClick={(e) => setScopeMenuAnchor(e.currentTarget)}
        >
          <span className="whitespace-nowrap text-lg font-bold tracking-[-0.01em] text-[#1C2228]">{scopeLabel}</span>
          <IconComponent name="BottomArrow" width="0.7rem" height="0.7rem" />
        </button>
        <div className="flex items-center gap-1">
          {!connected && (
            <span className="rounded-full bg-[#FBF0DE] px-2 py-0.5 text-xs text-[#C67C1A]">{t("Reconnecting…")}</span>
          )}
          <IconComponent
            name={maximized ? "exitFullScreen" : "enterFullScreen"}
            width="1.1rem"
            height="1.1rem"
            toolTipContent={maximized ? t("Restore panel") : t("Maximise panel")}
            onClick={onToggleMaximize}
          />
          <IconComponent name="CrossIcon" width="1rem" height="1rem" toolTipContent={t("Close")} onClick={onClose} />
        </div>
      </div>
      {scopeMenuAnchor && (
        <MenuComponent anchorEl={scopeMenuAnchor} open onClose={() => setScopeMenuAnchor(null)} width="14rem">
          {SCOPES.map((option) => (
            <MenuItemComponent
              key={option}
              onClick={() => {
                chat.setScope(option);
                setScopeMenuAnchor(null);
              }}
            >
              <span className="flex w-full items-center gap-2">
                <span className={`flex-1 ${option === scope ? "font-bold" : ""}`}>{scopeOptionLabel(option)}</span>
                {option === scope && (
                  <span className="rounded-full bg-[#4794BA] px-2 py-0.5 text-[0.65rem] font-bold text-white">
                    {t("Current")}
                  </span>
                )}
              </span>
            </MenuItemComponent>
          ))}
        </MenuComponent>
      )}

      {/* Row 2: Create / search / Unread toggle / section options */}
      <div className="flex items-center gap-2.5 px-4 pb-3">
        {searchOpen ? (
          <div className="relative min-w-0 flex-1">
            <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2">
              <IconComponent name="SearchIcon" width="0.9rem" height="0.9rem" />
            </span>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={SEARCH_MAX_LENGTH}
              placeholder={t("Search conversations")}
              className="h-9.5 w-full rounded-lg border border-[#4794BA] bg-white pe-9 ps-9 text-sm text-[#1C2228] outline-hidden"
              onKeyDown={(e) => {
                if (e.key === "Escape") closeSearch();
              }}
            />
            <button
              type="button"
              title={t("Close search")}
              onClick={closeSearch}
              className="absolute end-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[#EEF2F5]"
            >
              <IconComponent name="CrossIcon" width="0.6rem" height="0.6rem" />
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              data-testid="portalChatCreate"
              title={scope === "project" ? t("Project chats are created with the Space") : t("New chat")}
              onClick={onCreate}
              className="flex h-9.5 shrink-0 items-center gap-2 rounded-lg bg-[#F4FAFD] px-4 text-[0.8125rem] font-semibold text-[#1C2228] hover:bg-[#E4F1F9]"
            >
              <IconComponent name="plus-icon" width="0.9rem" height="0.9rem" />
              {t("Create")}
            </button>
            <span className="flex-1" />
            <IconComponent
              name="SearchIcon"
              width="1.1rem"
              height="1.1rem"
              toolTipContent={t("Find a conversation")}
              onClick={() => setSearchOpen(true)}
            />
          </>
        )}
        <ToggleSwitchComponent
          label={t("Unread")}
          labelPlacement="end"
          isChecked={unreadOnly}
          onChange={() => toggleUnreadOnly()}
        />
        {scope !== "all" && (
          <span
            role="button"
            tabIndex={-1}
            title={t("Section options")}
            className="rounded-md p-1 hover:bg-[#EEF2F5]"
            onClick={(e) => setBarMenuAnchor(e.currentTarget)}
            onKeyDown={() => undefined}
          >
            <IconComponent name="threedots" width="1rem" height="1rem" />
          </span>
        )}
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 px-3 pb-3">
        {chat.loadingList && !chat.conversations.length && (
          <div className="p-4 text-sm text-[#6B757D]">{t("Loading")}…</div>
        )}
        {!chat.loadingList && !listRows.length && renderEmptyState()}
        {listRows.length > 0 && (
          <VirtualizedList
            items={listRows}
            ariaLabel={t("Chats")}
            getKey={(row) => row.key}
            rowHeight={rowHeight}
            renderRow={renderListRow}
          />
        )}
      </div>

      {/* Row ••• — one flat menu (hide / mute / move to section) */}
      {menuFor && menuConversation && (
        <MenuComponent anchorEl={menuFor.anchor} open onClose={() => setMenuFor(null)} width="14rem">
          {menuConversation.unreadCount > 0 && (
            <MenuItemComponent
              onClick={() => {
                setMenuFor(null);
                chat.markConversationRead(menuConversation._id);
              }}
            >
              {t("Mark as read")}
            </MenuItemComponent>
          )}
          <MenuItemComponent
            onClick={() => {
              setMenuFor(null);
              if (menuConversation.hidden) chat.unhideAndOpen(menuConversation._id);
              else setHideCandidate(menuConversation);
            }}
          >
            {menuConversation.hidden ? t("Unhide chat") : t("Hide conversation")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              setMenuFor(null);
              void chat.updateConversationState(menuConversation._id, { muted: !menuConversation.muted });
              chat.showToast(menuConversation.muted ? t("Chat unmuted") : t("Chat muted"));
            }}
          >
            {menuConversation.muted ? t("Unmute") : t("Mute")}
          </MenuItemComponent>
          {moveToSectionItems}
        </MenuComponent>
      )}

      {/* Section sub-header ••• */}
      {sectionHeaderMenu && (
        <MenuComponent
          anchorEl={sectionHeaderMenu.anchor}
          open
          onClose={() => setSectionHeaderMenu(null)}
          width="13rem"
        >
          <MenuItemComponent
            onClick={() => {
              const { section } = sectionHeaderMenu;
              setSectionHeaderMenu(null);
              setSectionDialog({ scope: section.scope, section });
            }}
          >
            {t("Rename section")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              const { section } = sectionHeaderMenu;
              setSectionHeaderMenu(null);
              // PRD §4.9: deleting a section un-assigns its chats; it never deletes the chats.
              void deleteSectionRequest({ sectionId: toObjectIdString(String(section._id)) }).then(() => {
                chat.showToast(t("Section deleted"));
                return chat.refreshList();
              });
            }}
          >
            {t("Delete section")}
          </MenuItemComponent>
        </MenuComponent>
      )}

      {/* Project sub-header ••• */}
      {projectHeaderMenuAnchor && (
        <MenuComponent
          anchorEl={projectHeaderMenuAnchor}
          open
          onClose={() => setProjectHeaderMenuAnchor(null)}
          width="13rem"
        >
          <MenuItemComponent
            onClick={() => {
              setProjectHeaderMenuAnchor(null);
              setShowProjectOrder(true);
            }}
          >
            {t("Manage sections")}
          </MenuItemComponent>
        </MenuComponent>
      )}

      {/* Type-card ••• (All Chats) and the toolbar ••• (single-type views): create + manage */}
      {(typeHeaderMenu ?? barMenuAnchor) &&
        (() => {
          const type: ChatType = typeHeaderMenu
            ? typeHeaderMenu.type
            : scope === "project"
              ? "space"
              : scope === "group"
                ? "group"
                : "direct";
          const anchor = typeHeaderMenu ? typeHeaderMenu.anchor : barMenuAnchor;
          const close = () => {
            setTypeHeaderMenu(null);
            setBarMenuAnchor(null);
          };
          const sectionScope = sectionScopeOf(type);
          return (
            <MenuComponent anchorEl={anchor} open onClose={close} width="13rem">
              {sectionScope && (
                <MenuItemComponent
                  onClick={() => {
                    close();
                    setSectionDialog({ scope: sectionScope });
                  }}
                >
                  {t("Create a section")}
                </MenuItemComponent>
              )}
              <MenuItemComponent
                onClick={() => {
                  close();
                  if (sectionScope) setSectionsScope(sectionScope);
                  else setShowProjectOrder(true);
                }}
              >
                {t("Manage sections")}
              </MenuItemComponent>
            </MenuComponent>
          );
        })()}

      {showNewChat && <NewChatDialog onClose={() => setShowNewChat(false)} />}
      {sectionsScope && (
        <SectionsDialog
          scope={sectionsScope}
          onClose={() => setSectionsScope(null)}
          onCreate={() => setSectionDialog({ scope: sectionsScope })}
          onRename={(section) => setSectionDialog({ scope: sectionsScope, section })}
        />
      )}
      {showProjectOrder && <ProjectOrderDialog onClose={() => setShowProjectOrder(false)} />}
      {sectionDialog && <SectionDialog target={sectionDialog} onClose={() => setSectionDialog(null)} />}
      {hideCandidate && <HideConfirmDialog conversation={hideCandidate} onClose={() => setHideCandidate(null)} />}
    </div>
  );
}

export default ChatListPane;
